/**
 * Shared constants for Elections visualizations
 * Ensures consistent styling across all components
 */

// ============================================
// CANDIDATE COLORS - Consistent across all views
// ============================================
export const CANDIDATE_COLORS: Record<string, string> = {
    'José Antonio Kast': '#DC2626',        // Vibrant Red
    'Jeannette Jara': '#0D9488',           // Teal
    'Johannes Kaiser': '#2563EB',          // Royal Blue
    'Franco Parisi': '#06B6D4',            // Cyan (very different from red)
    'Eduardo Artés': '#CA8A04',            // Gold
    'Marco Enríquez-Ominami': '#9333EA',   // Purple
    'Harold Mayne-Nicholls': '#78716C',    // Warm Gray
    'Evelyn Matthei': '#FACC15',           // Yellow
};

// Candidate images mapping
export const CANDIDATE_IMAGES: Record<string, string> = {
    'José Antonio Kast':
        'https://firebasestorage.googleapis.com/v0/b/alojo-1da29.appspot.com/o/podscope%2Fcandidatos%2Fkast.jpg?alt=media&token=55c653a9-6a71-4cce-ac0b-35d695cc5073',
    'Jeannette Jara':
        'https://firebasestorage.googleapis.com/v0/b/alojo-1da29.appspot.com/o/podscope%2Fcandidatos%2Fjara.png?alt=media&token=f8f74baa-74ce-43ff-b307-10dd5aecfc0c',
    'Johannes Kaiser':
        'https://firebasestorage.googleapis.com/v0/b/alojo-1da29.appspot.com/o/podscope%2Fcandidatos%2Fkaiser.jpg?alt=media&token=82960440-72a3-44c6-8c19-4bf4829588eb',
    'Franco Parisi':
        'https://firebasestorage.googleapis.com/v0/b/alojo-1da29.appspot.com/o/podscope%2Fcandidatos%2Fparisi.png?alt=media&token=2dedd400-54da-43fb-bf85-3c9522990600',
    'Eduardo Artés':
        'https://firebasestorage.googleapis.com/v0/b/alojo-1da29.appspot.com/o/podscope%2Fcandidatos%2Fartes.jpg?alt=media&token=8b14c261-3401-4a2e-9e24-1c8423fa5566',
    'Marco Enríquez-Ominami':
        'https://firebasestorage.googleapis.com/v0/b/alojo-1da29.appspot.com/o/podscope%2Fcandidatos%2Fominami.jpeg?alt=media&token=edfc0801-58fa-4af0-9a0d-31e73d42bf19',
    'Harold Mayne-Nicholls':
        'https://firebasestorage.googleapis.com/v0/b/alojo-1da29.appspot.com/o/podscope%2Fcandidatos%2Fharold.jpeg?alt=media&token=ca834eee-20bd-46bb-8767-d9ceae74cede',
    'Evelyn Matthei':
        'https://firebasestorage.googleapis.com/v0/b/alojo-1da29.appspot.com/o/podscope%2Fcandidatos%2Fmatthei.jpeg?alt=media&token=0edb7d1a-b075-4304-a344-9b4dbda083d6',
};

// ============================================
// TOPIC COLORS - Consistent across all views
// ============================================
export const TOPIC_COLORS: Record<string, string> = {
    'economía': '#DC2626',               // Red
    'seguridad': '#0EA5E9',              // Sky Blue
    'elecciones': '#14B8A6',             // Teal
    'salud': '#22C55E',                  // Green
    'educación': '#EAB308',              // Yellow
    'pensiones': '#A855F7',              // Purple
    'derechos de la mujer': '#F59E0B',   // Amber
    'energía y minería': '#7C2D12',      // Dark Brown
    'vivienda': '#06B6D4',               // Cyan
    'inmigración': '#0D9488',            // Teal (distinct from red)
    'medio ambiente': '#84CC16',         // Lime
    'justicia': '#F97316',               // Orange
    'trabajo': '#6366F1',                // Indigo
    'transparencia': '#10B981',          // Emerald
    'derechos humanos': '#8B5CF6',       // Violet
    'agricultura y ruralidad': '#92400E', // Brown
    'defensa nacional': '#475569',       // Slate
    'memoria y dictadura': '#7C3AED',    // Purple
    'pueblos originarios': '#65A30D',    // Light Green
    'relaciones internacionales': '#0284C7', // Light Blue
    'tecnología y digitalización': '#16A34A', // Medium Green
    'infraestructura': '#D97706',        // Amber Dark
    'partidos políticos': '#DB2777',     // Pink
    'temas sociales': '#3B82F6',         // Blue
    'gobernanza': '#7C3AED',             // Deep Violet
    'encuestas': '#C084FC',              // Light Purple
    'otros': '#6B7280',                  // Gray
};

// ============================================
// HELPER FUNCTIONS
// ============================================
export const getColorForCandidate = (name: string, index: number): string => {
    if (CANDIDATE_COLORS[name]) return CANDIDATE_COLORS[name];
    const fallbackColors = ['#3B82F6', '#EF4444', '#22C55E', '#A855F7', '#F97316', '#06B6D4', '#78716C', '#64748B'];
    return fallbackColors[index % fallbackColors.length];
};

export const getColorForTopic = (name: string, index: number): string => {
    if (TOPIC_COLORS[name]) return TOPIC_COLORS[name];
    const fallbackColors = ['#3B82F6', '#EF4444', '#22C55E', '#A855F7', '#F97316', '#06B6D4', '#EC4899', '#64748B'];
    return fallbackColors[index % fallbackColors.length];
};

/**
 * Capitalize topic names with Spanish title case rules
 * First word always capitalized, subsequent words lowercase if they are articles/prepositions
 */
export const capitalizeTopicName = (name: string): string => {
    if (!name) return '';
    
    // Words that should remain lowercase in Spanish title case (except at start)
    const lowercaseWords = ['y', 'de', 'la', 'el', 'del', 'en', 'a', 'con', 'por', 'para'];
    
    return name
        .split(' ')
        .map((word, index) => {
            // First word is always capitalized
            if (index === 0) {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            }
            // Check if word should remain lowercase
            if (lowercaseWords.includes(word.toLowerCase())) {
                return word.toLowerCase();
            }
            // Capitalize other words
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
};

// ============================================
// CHART STYLING
// ============================================
export const CHART_STYLES = {
    // Card styling
    card: {
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.06)',
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)',
    },
    // Grid styling
    grid: {
        stroke: '#E5E7EB',
        strokeDasharray: '3 3',
    },
    // Axis styling
    axis: {
        tick: {
            fontSize: 11,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fill: '#6B7280',
        },
        axisLine: {
            stroke: '#D1D5DB',
        },
    },
    // Tooltip styling
    tooltip: {
        contentStyle: {
            backgroundColor: 'rgba(255,255,255,0.98)',
            border: 'none',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            padding: '12px 16px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        },
        labelStyle: {
            fontWeight: 600,
            color: '#1F2937',
            marginBottom: 8,
        },
        itemStyle: {
            padding: '4px 0',
        },
    },
    // Legend styling
    legend: {
        wrapperStyle: {
            paddingTop: 20,
        },
    },
};

// ============================================
// ELECTION MILESTONES
// ============================================
export const ELECTION_MILESTONES = [
    { id: 'primaries', date: '2025-06-29', color: '#8B5CF6' },
    { id: 'firstRound', date: '2025-11-16', color: '#EC4899' },
    { id: 'secondRound', date: '2025-12-14', color: '#EC4899' },
] as const;

// ============================================
// HEATMAP UTILITIES
// ============================================
export const getHeatmapColor = (value: number, max: number): string => {
    if (value === 0 || max <= 0) return '#1C1B1F';
    const intensity = Math.min(value / max, 1);
    const h = 217;
    const s = 42 + intensity * 48;
    const l = 16 + intensity * 46;
    return `hsl(${h}, ${s}%, ${l}%)`;
};

// Alternative warm heatmap colors (red-orange gradient)
export const getHeatmapColorWarm = (value: number, max: number): string => {
    if (value === 0) return '#FEF2F2';
    const intensity = Math.min(value / max, 1);
    const h = 15 - (intensity * 10); // Hue from 15 (orange) to 5 (red)
    const s = 85 + (intensity * 15); // Saturation 85-100%
    const l = 95 - (intensity * 45); // Lightness 95% to 50%
    return `hsl(${h}, ${s}%, ${l}%)`;
};

